import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { BusTwoIcon } from '@repo/ui/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getCoachCompany } from '../../../api/ols/coach-companies/get-coach-comapny'
import { updateCoachCompany } from '../../../api/ols/coach-companies/update-coach-companies'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import UserNavBar from '../../../components/elements/nav-bar'
import ArrivalAndDepartureTable from '../../../components/pages/ols/coach-companies/arrival-and-departure/arrival-and-departure-table'
import CoachCompanyForm from '../../../components/pages/ols/coach-companies/coach-company-form'
import CulturalOutingTable from '../../../components/pages/ols/coach-companies/cultural-outing/cultural-outing-table'
import QuotationSection from '../../../components/pages/ols/quotation/quotation-section'
import { getOlPermissions } from '../../../lib/constants/ol-permissions'
import { Paths } from '../../../lib/constants/path'
import useAuth from '../../../lib/hooks/use-auth'
import { updateButtonsConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import NotFoundPage from '../../common/not-found'
import { mapCoachCompanyToInput } from './util'

const CoachCompaniesUpdatePage = () => {
  const { id } = useParams<{ id: string }>()
  const { navigate, back } = useNavigator()
  const { user } = useAuth()
  const olId = user?.ol?.id
  const queryClient = useQueryClient()

  const { mutateAsync } = useMutation({
    mutationFn: updateCoachCompany,
    onError: showAxiosError,
    onSuccess: () => {
      queryClient.invalidateQueries({ exact: true, queryKey: ['coach-companies', id, olId] })
      toast.success('Coach company updated successfully')
    }
  })

  const { data: coachComapny, isPending } = useQuery({
    queryKey: ['coach-companies', id, olId],
    queryFn: () => {
      if (!id || isNaN(+id) || !olId) return
      return getCoachCompany({ id: +id, olId })
    },
    enabled: !!id && !!olId
  })

  const permissions = getOlPermissions({ role: user.role, type: user?.ol?.type })

  if (!olId || isPending) return <LoadingScreen />
  if (!coachComapny) return <NotFoundPage />

  return (
    <div>
      <UserNavBar title={'Modify coach company'} icon={BusTwoIcon} />
      <div className={'h-4'} />
      <div className="flex flex-col gap-5">
        <CoachCompanyForm
          country={user?.ol?.country ?? 'FR'}
          defaultValues={mapCoachCompanyToInput(coachComapny)}
          buttonsConfig={updateButtonsConfig({
            onCancel: () => back(Paths.OL_PERSONAL_SPACE_COACH_COMPANIES_LIST_PAGE),
            onUpdate: (values) => {
              if (!id || isNaN(+id) || !olId) return
              return mutateAsync({ id: +id, ...values, olId })
            },
            onUpdateThenCreate: async (values) => {
              if (!id || isNaN(+id) || !olId) return
              await mutateAsync({ id: +id, ...values, olId })
              navigate(Paths.OL_PERSONAL_SPACE_COACH_COMPANIES_CREATE_PAGE)
            },
            onUpdateThenList: async (values) => {
              if (!id || isNaN(+id) || !olId) return
              await mutateAsync({ id: +id, ...values, olId })
              navigate(Paths.OL_PERSONAL_SPACE_COACH_COMPANIES_LIST_PAGE)
            },
            isEnglish: true
          })}
        />
        {permissions['coach-company-full-view'] && (
          <Box className="flex flex-col gap-4">
            {user.ol?.id && (
              <CulturalOutingTable coachCompanyId={coachComapny.id} olId={user?.ol?.id} />
            )}
            <ArrivalAndDepartureTable
              currency={user?.ol?.currency}
              coachCompanyId={coachComapny.id}
              country={user?.ol?.country ?? 'France'}
            />
            <QuotationSection mode="coachCompany" coachCompanyId={coachComapny.id} />
          </Box>
        )}
      </div>
    </div>
  )
}

export default CoachCompaniesUpdatePage
